import React from 'react';
import { CustomModal } from '../../../../common/CustomModal';
import { CheckCircle, XCircle, X, ArrowLeft, FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-toastify';
import '../../../../../styles/components/action-modal.scss';
import { StatusNewBadge } from '../../../../common/StatusBadge';
import { NEW_TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../../../../constants/status';
import i18n from '../../../../../i18n';
import { formatDate } from '../../../../../utils/common';
import { rechargeAction, withdrawalAction } from '../services/transactionService';
import { imageUrl } from '../../../../../constants/global';

export class ActionModal extends React.Component {
  constructor() {
    super();
    this.state = {
      submitting: false,
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',
    };
  }

  handleApprove = async () => {
    const { selectedTransaction, onComplete } = this.props;

    try {
      this.setState({ submitting: true });
      const payload = {
        id: selectedTransaction.id,
        action: 'approve',
      };

      const apiMap = {
        wallet_recharge: rechargeAction,
        withdraw: withdrawalAction,
      };

      const api = apiMap[selectedTransaction.type] || rechargeAction;

      const response = await api(payload);

      if (response && response.success) {
        toast.success(response.message || 'Payment approved successfully');
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response.message || i18n.t('something_went_wrong'));
      }
    } catch (error) {
      toast.error(i18n.t('something_went_wrong'));
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleReject = async () => {
    const { selectedTransaction, onComplete } = this.props;
    const { rejectReason } = this.state;

    // Validation
    if (!rejectReason.trim()) {
      this.setState({ rejectError: 'Reject reason is required' });
      return;
    }

    try {
      this.setState({ submitting: true, rejectError: '' });
      const payload = {
        id: selectedTransaction.id,
        action: 'reject',
        reason: rejectReason.trim(),
      };

      const response = await rechargeAction(payload);
      if (response.success) {
        toast.success(response.message || 'Payment rejected successfully');
        onComplete?.();
        this.handleClose();
      } else {
        toast.error(response.message || 'Failed to reject payment');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleRejectClick = () => {
    this.setState({ showRejectInput: true });
  };

  handleClose = () => {
    this.setState({
      rejectReason: '',
      showRejectInput: false,
      rejectError: '',
      submitting: false,
    });
    this.props.onClose();
  };

  render() {
    const { show, selectedTransaction } = this.props;
    const { rejectReason, submitting, showRejectInput, rejectError } = this.state;

    if (!selectedTransaction) return null;

    return (
      <CustomModal isOpen={show} header={`Transaction Action Required`} onClose={this.handleClose}>
        <div className="action-modal-container">
          {/* Payment Details Section */}
          <div className="payment-details-card">
            <h6>Transaction Details</h6>
            <div className="details-grid">
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('bidder_id')}</span>
                <span className="detail-value success">{selectedTransaction.user || '--'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('name')}</span>
                <span className="detail-value ">
                  {selectedTransaction?.user_relation?.name || '--'}
                </span>
              </div>

              <div className="detail-item-new">
                <span className="detail-label">
                  {selectedTransaction.type === 'wallet_recharge'
                    ? i18n.t('credit')
                    : i18n.t('debit')}
                </span>
                <span className="detail-value danger">
                  {selectedTransaction.type === 'wallet_recharge'
                    ? selectedTransaction.credit || '-'
                    : selectedTransaction.debit || '-'}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Balance</span>
                <span className="detail-value danger">{selectedTransaction.balance || '-'}</span>
              </div>

              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('bank')}</span>
                <span className="detail-value ">{selectedTransaction.bank || '--'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('ac_no')}</span>
                <span className="detail-value ">{selectedTransaction.account_number || '-'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('receipt_number')}</span>
                <span className="detail-value ">{selectedTransaction.receipt_number || '-'}</span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('receipt_date')}</span>
                <span className="detail-value">
                  {selectedTransaction.payment_date
                    ? formatDate(selectedTransaction.payment_date)
                    : '--'}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Receipt</span>
                <span className={`detail-value  `}>
                  {selectedTransaction.file_receipt ? (
                    <a
                      className="button-table"
                      href={`${imageUrl}uploads/wallet/receipt/${selectedTransaction.file_receipt}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileSpreadsheet size={18} /> {i18n.t('receipt')}
                    </a>
                  ) : (
                    <span className="attachment-empty">No Receipt</span>
                  )}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">Status</span>
                <span className={`detail-value  `}>
                  <StatusNewBadge status={NEW_TRANSACTION_STATUS[selectedTransaction.status]} />
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('type')}</span>
                <span className="detail-value amount">
                  {TRANSACTION_TYPES[selectedTransaction.type]?.text ||
                    selectedTransaction.type ||
                    '-'}
                </span>
              </div>
              <div className="detail-item-new">
                <span className="detail-label">{i18n.t('method')}</span>
                <span className="detail-value ">{selectedTransaction.method || '-'}</span>
              </div>
            </div>
          </div>

          {/* Reject Reason Section - Shows when reject is clicked */}
          {showRejectInput && (
            <div className="reject-section">
              <label htmlFor="rejectReason" className="reject-label">
                Reason for Rejection <span className="required">*</span>
              </label>
              <textarea
                id="rejectReason"
                className={`reject-textarea ${rejectError ? 'error' : ''}`}
                value={rejectReason}
                onChange={(e) =>
                  this.setState({
                    rejectReason: e.target.value,
                    rejectError: e.target.value.trim() ? '' : 'Reject reason is required',
                  })
                }
                placeholder="Enter the reason for rejecting this payment..."
                rows={4}
                disabled={submitting}
              />
              {rejectError && <span className="error-message">{rejectError}</span>}
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons-container">
            {!showRejectInput ? (
              <>
                <button
                  className="modal-btn btn-cancel"
                  onClick={this.handleClose}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  className="modal-btn btn-danger"
                  onClick={this.handleRejectClick}
                  disabled={submitting}
                >
                  <X size={18} />
                  Reject
                </button>

                <button
                  className="modal-btn btn-success"
                  onClick={this.handleApprove}
                  disabled={submitting}
                >
                  <CheckCircle size={18} />
                  {submitting ? 'Approving...' : 'Approve'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="modal-btn btn-cancel"
                  onClick={() =>
                    this.setState({ showRejectInput: false, rejectReason: '', rejectError: '' })
                  }
                  disabled={submitting}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                <button
                  className="modal-btn btn-confirm"
                  onClick={this.handleReject}
                  disabled={submitting || !rejectReason.trim()}
                >
                  <XCircle size={18} />
                  {submitting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </>
            )}
          </div>
        </div>
      </CustomModal>
    );
  }
}
